/**
 * PerformanceMonitor.ts
 * 
 * Simplified performance monitoring for Portfolio-3Topf simulations.
 * Provides basic execution time tracking for optimization and debugging.
 * 
 * SIMPLIFIED VERSION: Reduced from comprehensive monitoring system to essential timer utility.
 * Advanced features (memory tracking, alerts, trends) removed as they were not utilized.
 * 
 * @author Portfolio-3Topf Team
 * @version 2.0.0 (Simplified)
 * @since 2024-12-28
 */

/**
 * Basic performance metrics for a single measurement
 */
export interface PerformanceMetrics {
  /** Unique identifier for this measurement */
  id: string;
  /** Timestamp when measurement started (milliseconds) */
  startTime: number;
  /** Timestamp when measurement ended (milliseconds) */
  endTime: number;
  /** Total execution time in milliseconds */
  duration: number;
  /** Type of operation being measured */
  operationType: 'simulation' | 'monte-carlo' | 'calculation' | 'export' | 'import';
  /** Number of iterations/years in simulation (if applicable) */
  iterations?: number;
  /** Additional context data */
  context?: Record<string, any>;
}

/**
 * PerformanceMonitor - Lightweight performance tracking utility
 * 
 * Provides simple start/stop timer functionality for measuring execution times
 * of simulation operations. Maintains a limited history for analysis.
 */
export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private metrics: PerformanceMetrics[] = [];
  private activeTimers: Map<string, { startTime: number; operationType: PerformanceMetrics['operationType']; context?: Record<string, any> }> = new Map();
  private maxMetricsHistory = 100; // Keep last 100 measurements

  private constructor() {
    // Singleton pattern
  }

  /**
   * Get singleton instance of PerformanceMonitor
   */
  public static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  /**
   * Start performance measurement for an operation
   * 
   * @param id - Unique identifier for this measurement
   * @param operationType - Type of operation being measured
   * @param context - Optional context data
   */
  public startMeasurement(
    id: string,
    operationType: PerformanceMetrics['operationType'],
    context?: Record<string, any>
  ): void {
    const startTime = performance.now();
    
    this.activeTimers.set(id, {
      startTime,
      operationType,
      context,
    });
  }

  /**
   * End performance measurement and record metrics
   * 
   * @param id - Unique identifier for the measurement to end
   * @param contextUpdates - Optional updates to context (e.g., actual iterations)
   * @returns PerformanceMetrics object or null if timer not found
   */
  public endMeasurement(id: string, contextUpdates?: Record<string, any>): PerformanceMetrics | null {
    const timer = this.activeTimers.get(id);
    if (!timer) {
      return null;
    }

    const endTime = performance.now();
    const duration = endTime - timer.startTime;

    // Merge context with updates
    const finalContext = { ...timer.context, ...contextUpdates };

    // Extract iterations from context if available (years field for simulations, or iterations override)
    const iterations = contextUpdates?.iterations ?? finalContext?.years;

    const metrics: PerformanceMetrics = {
      id,
      startTime: timer.startTime,
      endTime,
      duration,
      operationType: timer.operationType,
      iterations,
      context: finalContext,
    };

    // Store metrics
    this.metrics.push(metrics);
    this.activeTimers.delete(id);

    // Maintain metrics history limit
    if (this.metrics.length > this.maxMetricsHistory) {
      this.metrics = this.metrics.slice(-this.maxMetricsHistory);
    }

    return metrics;
  }

  /**
   * Convenience method to measure a function execution
   * 
   * @param id - Unique identifier
   * @param operationType - Type of operation
   * @param fn - Function to measure
   * @param context - Optional context data
   * @returns Object containing function result and metrics
   */
  public async measureFunction<T>(
    id: string,
    operationType: PerformanceMetrics['operationType'],
    fn: () => Promise<T> | T,
    context?: Record<string, any>
  ): Promise<{ result: T; metrics: PerformanceMetrics }> {
    this.startMeasurement(id, operationType, context);
    
    try {
      const result = await fn();
      const metrics = this.endMeasurement(id);
      
      return {
        result,
        metrics: metrics!,
      };
    } catch (error) {
      // End measurement even if function fails
      this.endMeasurement(id);
      throw error;
    }
  }

  /**
   * Get all recorded metrics
   * 
   * @returns Array of all performance metrics
   */
  public getAllMetrics(): PerformanceMetrics[] {
    return [...this.metrics];
  }

  /**
   * Get metrics filtered by operation type
   * 
   * @param operationType - Type of operation to filter by
   * @returns Array of filtered metrics
   */
  public getMetricsByType(operationType: PerformanceMetrics['operationType']): PerformanceMetrics[] {
    return this.metrics.filter(m => m.operationType === operationType);
  }

  /**
   * Get average duration for a specific operation type
   * 
   * @param operationType - Type of operation
   * @returns Object with average duration and count
   */
  public getAveragePerformance(
    operationType: PerformanceMetrics['operationType']
  ): { averageDuration: number; count: number } {
    const filteredMetrics = this.metrics.filter(m => m.operationType === operationType);

    if (filteredMetrics.length === 0) {
      return { averageDuration: 0, count: 0 };
    }

    const averageDuration = filteredMetrics.reduce((sum, m) => sum + m.duration, 0) / filteredMetrics.length;
    
    return {
      averageDuration,
      count: filteredMetrics.length,
    };
  }

  /**
   * Generate a performance report with summary statistics
   * 
   * @returns Performance report with aggregated metrics
   */
  public generateReport(): {
    totalMeasurements: number;
    averageDuration: number;
    breakdown: Record<string, { count: number; averageDuration: number }>;
  } {
    const totalMeasurements = this.metrics.length;
    const averageDuration = totalMeasurements > 0
      ? this.metrics.reduce((sum, m) => sum + m.duration, 0) / totalMeasurements
      : 0;

    // Create breakdown by operation type
    const breakdown: Record<string, { count: number; averageDuration: number }> = {};
    const operationTypes = new Set(this.metrics.map(m => m.operationType));
    
    operationTypes.forEach(type => {
      const typeMetrics = this.getMetricsByType(type);
      const typeAvg = typeMetrics.reduce((sum, m) => sum + m.duration, 0) / typeMetrics.length;
      breakdown[type] = {
        count: typeMetrics.length,
        averageDuration: typeAvg,
      };
    });

    return {
      totalMeasurements,
      averageDuration,
      breakdown,
    };
  }

  /**
   * Clear all metrics and active timers
   * Useful for testing or resetting state
   */
  public clearMetrics(): void {
    this.metrics = [];
    this.activeTimers.clear();
  }
}

// Export singleton instance for easy access
export const performanceMonitor = PerformanceMonitor.getInstance();
